// KIO-03: límite de espera del cobro y consulta final antes de rendirse.
//
// El cobro se corta con un AbortController (mismo patrón que SetupWizard y SecretMenu): al vencer
// la espera + la holgura se aborta, lo que detiene el polling y también la petición que esté en
// curso. Un timer por sí solo no corta un fetch colgado; el signal sí.
//
// Antes de dar el cobro por perdido se hace una consulta final, para no mostrar "tiempo agotado"
// sobre un pago que el POS alcanzó a aprobar.

// Getnet en modo síncrono deja la petición de venta abierta hasta 90 s antes de cancelarla por
// timeout (documentación C2C). El tótem no puede rendirse antes que el proveedor.
export const PROVIDER_SYNC_TIMEOUT_SECONDS = 90;

// Margen sobre la espera para que una aprobación que llegue tarde (demora de la C2C o de la red)
// no se pierda. Solo sirve para que el tótem no quede congelado: mientras más grande, mejor.
export const PAYMENT_GRACE_SECONDS = 30;

// Motivos con que se aborta el cobro. Solo 'timeout' hace la consulta final: si el cobro se
// abortó porque se cerró el modal, no hay nadie a quien mostrarle el resultado.
export const ABORT_TIMEOUT = 'timeout';
export const ABORT_CLOSED = 'closed';

export const PENDING_STATUSES = ['created', 'at_terminal'];

/**
 * Milisegundos desde el inicio del cobro hasta abortarlo: la espera configurada (o la del
 * proveedor, si es mayor) más la holgura.
 *
 * @param {number} paymentTimeoutSeconds - Espera configurada (style.global.checkout).
 */
export function getPaymentAbortMs(paymentTimeoutSeconds, graceSeconds = PAYMENT_GRACE_SECONDS) {
  const waitSeconds = Math.max(paymentTimeoutSeconds || 0, PROVIDER_SYNC_TIMEOUT_SECONDS);
  return (waitSeconds + graceSeconds) * 1000;
}

/**
 * Resultado de un carro según su estado: 'paid', 'pending' o 'canceled'. Sin carro, 'pending'
 * (todavía no se sabe nada).
 */
export function getPaymentOutcome(cart) {
  if (!cart) return 'pending';
  const status = cart.checkout_status;
  if (cart.processed_sale === true || status === 'processed' || status === 'paid') return 'paid';
  if (!status || PENDING_STATUSES.includes(status)) return 'pending';
  return 'canceled';
}

const isTimeoutAbort = (signal) => signal?.aborted && signal.reason === ABORT_TIMEOUT;

/**
 * Espera `ms` o hasta que se aborte `signal`, lo que ocurra primero. Nunca rechaza.
 */
export function sleep(ms, signal) {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const timer = setTimeout(done, ms);
    function done() {
      clearTimeout(timer);
      signal?.removeEventListener('abort', done);
      resolve();
    }
    signal?.addEventListener('abort', done, { once: true });
  });
}

/**
 * SOK-04: espera hasta que `predicate()` sea verdadero, revisando cada `intervalMs`. Devuelve
 * `false` si antes se aborta `signal` (se cerró el modal o se acabó la espera máxima). Nunca rechaza.
 */
export async function waitUntil(predicate, { signal, intervalMs = 1000 } = {}) {
  while (!signal?.aborted) {
    if (predicate()) return true;
    await sleep(intervalMs, signal);
  }
  return false;
}

/**
 * Signal que se aborta cuando se aborta `signal` o pasan `ms`. Así una petición colgada no se come
 * toda la espera. `clear` suelta el timer y el listener.
 */
export function withTimeout(signal, ms) {
  const controller = new AbortController();
  const abort = () => controller.abort(signal?.reason);
  const timer = setTimeout(() => controller.abort('request_timeout'), ms);
  if (signal?.aborted) abort();
  else signal?.addEventListener('abort', abort, { once: true });
  return {
    signal: controller.signal,
    clear: () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
    },
  };
}

// Una consulta con su propio límite. Si el `check` no respeta el signal (p. ej. una lectura de
// Firestore), igual se deja de esperar al vencer.
async function checkOnce(check, signal, timeoutMs) {
  const request = withTimeout(signal, timeoutMs);
  try {
    const aborted = new Promise((_, reject) => {
      if (request.signal.aborted) reject(request.signal.reason);
      request.signal.addEventListener('abort', () => reject(request.signal.reason), { once: true });
    });
    aborted.catch(() => {});
    return await Promise.race([check(request.signal), aborted]);
  } finally {
    request.clear();
  }
}

/**
 * Consulta final: hasta `attempts` consultas separadas por `spacingMs`. Se detiene en cuanto el
 * carro deja de estar pendiente. No recibe el signal del cobro (que ya está abortado): cada
 * consulta tiene solo su propio límite.
 *
 * @returns {Promise<{outcome: 'paid'|'canceled'|'timeout', data: object|null}>}
 */
export async function finalPaymentCheck({ check, attempts = 3, spacingMs = 3000, requestTimeoutMs = 5000, lastData = null }) {
  let data = lastData;
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (attempt > 0) await sleep(spacingMs);
    try {
      const cart = await checkOnce(check, null, requestTimeoutMs);
      if (cart) data = cart;
      const outcome = getPaymentOutcome(cart);
      if (outcome !== 'pending') return { outcome, data };
    } catch (error) {
      console.warn('Consulta final del pago falló:', error);
    }
  }
  return { outcome: 'timeout', data };
}

/**
 * Consulta el estado del cobro hasta que deje de estar pendiente o se aborte `signal`.
 *
 * - Errores de red o de una consulta: se reintenta, no se corta el cobro.
 * - Intervalo suave: parte en `intervalMs` y sube de a 250 ms hasta `maxIntervalMs`.
 * - Abortado por tiempo ('timeout'): hace la consulta final con `finalCheck` (por defecto `check`).
 * - Abortado por otro motivo (se cerró el modal): devuelve 'aborted' sin consultar más.
 *
 * @param {object} options
 * @param {(signal: AbortSignal) => Promise<object|null>} options.check - Devuelve el carro (o null si aún no se sabe).
 * @param {AbortSignal} options.signal - Se aborta con ABORT_TIMEOUT o ABORT_CLOSED.
 * @param {object} [options.lastData] - Último carro conocido, por si nunca llega otro.
 * @returns {Promise<{outcome: 'paid'|'canceled'|'timeout'|'aborted', data: object|null}>}
 */
export async function waitForPayment({
  check,
  signal,
  lastData = null,
  intervalMs = 1000,
  maxIntervalMs = 2000,
  requestTimeoutMs = 10000,
  finalCheck = check,
  finalAttempts = 3,
  finalSpacingMs = 3000,
  finalRequestTimeoutMs = 5000,
  onFinalCheck,
}) {
  let data = lastData;
  let interval = intervalMs;

  while (!signal?.aborted) {
    try {
      const cart = await checkOnce(check, signal, requestTimeoutMs);
      if (cart) data = cart;
      const outcome = getPaymentOutcome(cart);
      if (outcome !== 'pending') return { outcome, data };
    } catch (error) {
      if (!signal?.aborted) console.warn('Consulta del estado del pago falló, se reintenta:', error);
    }
    await sleep(interval, signal);
    interval = Math.min(maxIntervalMs, interval + 250);
  }

  if (!isTimeoutAbort(signal)) return { outcome: 'aborted', data };

  onFinalCheck?.();
  return finalPaymentCheck({
    check: finalCheck,
    attempts: finalAttempts,
    spacingMs: finalSpacingMs,
    requestTimeoutMs: finalRequestTimeoutMs,
    lastData: data,
  });
}
